import { useState, useEffect, useCallback } from 'react'
import ictaApi from '../api/ictaApi'
import {
	APIError,
	Crop,
	GetCropsResponse,
	CreateCropPayload,
	CreateCropResponse,
	AddGermplasmPayload,
	AddGermplasmResponse,
	RemoveGermplasmResponse,
	UpdateCropPayload,
	UpdateCropResponse,
	DeleteCropResponse
} from '../models/crop'

// Helper para parsear errores
const parseErrors = (errors?: APIError[]): string | null => {
	if (!errors || errors.length === 0) return null
	return errors[0].msg || 'Error desconocido'
}

export const useCrop = () => {
	const [crops, setCrops] = useState<Crop[]>([])
	const [total, setTotal] = useState<number>(0)
	const [selectedCrop, setSelectedCrop] = useState<Crop | null>(null)

	const [loadingList, setLoadingList] = useState<boolean>(false)
	const [loadingItem, setLoadingItem] = useState<boolean>(false)
	const [creating, setCreating] = useState<boolean>(false)
	const [adding, setAdding] = useState<boolean>(false)
	const [removing, setRemoving] = useState<boolean>(false)
	const [updating, setUpdating] = useState<boolean>(false)
	const [deleting, setDeleting] = useState<boolean>(false)

	const [error, setError] = useState<string | null>(null)

	// Listar cultivos
	const fetchCrops = useCallback(async () => {
		setLoadingList(true)
		setError(null)
		try {
			const { data } = await ictaApi.get<GetCropsResponse>('/crop')
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
			} else {
				setCrops(data.crops)
				setTotal(data.total)
			}
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener cultivos')
		} finally {
			setLoadingList(false)
		}
	}, [])

	// Crear cultivo
	const createCrop = useCallback(async (payload: CreateCropPayload) => {
		setCreating(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<CreateCropResponse>('/crop', payload)
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
				return null
			}
			if (data.crop) {
				setCrops((prev) => [data.crop!, ...prev])
				return data.crop
			}
			return null
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al crear cultivo')
			return null
		} finally {
			setCreating(false)
		}
	}, [])

	// Agregar germoplasma
	const addGermplasm = useCallback(
		async (cropId: string, payload: AddGermplasmPayload) => {
			setAdding(true)
			setError(null)
			try {
				const { data } = await ictaApi.post<AddGermplasmResponse>(
					`/crop/add-germplasm/${cropId}`,
					payload
				)
				const errMsg = parseErrors(data.errors)
				if (errMsg) {
					setError(errMsg)
					return null
				}
				if (data.crop) {
					setCrops((prev) => prev.map((c) => (c.uid === cropId ? data.crop! : c)))
					if (selectedCrop?.uid === cropId) setSelectedCrop(data.crop)
					return data.crop
				}
				return null
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al agregar germoplasma')
				return null
			} finally {
				setAdding(false)
			}
		},
		[selectedCrop]
	)

	// Eliminar germoplasma
	const removeGermplasm = useCallback(
		async (cropId: string, payload: AddGermplasmPayload) => {
			setRemoving(true)
			setError(null)
			try {
				const { data } = await ictaApi.delete<RemoveGermplasmResponse>(
					`/crop/remove-germplasm/${cropId}`,
					{ data: payload }
				)
				const errMsg = parseErrors(data.errors)
				if (errMsg) {
					setError(errMsg)
					return null
				}
				if (data.crop) {
					setCrops((prev) => prev.map((c) => (c.uid === cropId ? data.crop! : c)))
					if (selectedCrop?.uid === cropId) setSelectedCrop(data.crop)
					return data.crop
				}
				return null
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al eliminar germoplasma')
				return null
			} finally {
				setRemoving(false)
			}
		},
		[selectedCrop]
	)

	// Obtener un cultivo
	const fetchCrop = useCallback(async (id: string) => {
		setLoadingItem(true)
		setError(null)
		try {
			const { data } = await ictaApi.get<GetCropsResponse>(`/crop/${id}`)
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
			} else if (data.crops && data.crops.length > 0) {
				setSelectedCrop(data.crops[0])
			}
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener cultivo')
		} finally {
			setLoadingItem(false)
		}
	}, [])

	// Editar cultivo
	const updateCrop = useCallback(
		async (cropId: string, payload: UpdateCropPayload) => {
			setUpdating(true)
			setError(null)
			try {
				const { data } = await ictaApi.put<UpdateCropResponse>(`/crop/${cropId}`, payload)
				const errMsg = parseErrors(data.errors)
				if (errMsg) {
					setError(errMsg)
					return null
				}
				if (data.crop) {
					setCrops((prev) => prev.map((c) => (c.uid === cropId ? data.crop! : c)))
					if (selectedCrop?.uid === cropId) setSelectedCrop(data.crop)
					return data.crop
				}
				return null
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al editar cultivo')
				return null
			} finally {
				setUpdating(false)
			}
		},
		[selectedCrop]
	)

	// Eliminar cultivo
	const deleteCrop = useCallback(async (cropId: string) => {
		setDeleting(true)
		setError(null)
		try {
			const { data } = await ictaApi.delete<DeleteCropResponse>(`/crop/${cropId}`)
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
				return false
			}
			setCrops((prev) => prev.filter((c) => c.uid !== cropId))
			return true
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al eliminar cultivo')
			return false
		} finally {
			setDeleting(false)
		}
	}, [])

	// Auto-fetch al montar
	useEffect(() => {
		fetchCrops()
	}, [fetchCrops])

	// Alert de error
	useEffect(() => {
		if (error) {
			alert(`Error: ${error}`)
			setError(null) // Reset error after showing alert
		}
	}, [error])

	return {
		crops,
		total,
		selectedCrop,
		loadingList,
		loadingItem,
		creating,
		adding,
		removing,
		updating,
		deleting,
		error,
		fetchCrops,
		fetchCrop,
		createCrop,
		addGermplasm,
		removeGermplasm,
		updateCrop,
		deleteCrop
	}
}
